import React, { useEffect, useMemo, useRef, useState } from 'react';
import { netlabApi } from './api';

const LEVELS = [
  { key: 'mv', label: 'MV grid' },
  { key: 'lv', label: 'LV grids' },
  { key: 'merged', label: 'MV + LV merged' },
];

// "KG_Gemeinde_Bundesland" -> parts (same convention as NetBankPage).
function nameParts(name) {
  const p = (name || '').split('_');
  return { kg: p[0] || name || '', gemeinde: p[1] || '', bundesland: p.slice(2).join('_') || '' };
}

export default function NetbankBrowser({ onLoaded, onClose, preselect }) {
  const [areas, setAreas] = useState(null);
  const [areaError, setAreaError] = useState('');
  const [search, setSearch] = useState('');
  // A preselected area is known by id only until the area list arrives.
  const [area, setArea] = useState(() => (preselect?.task ? { task_id: preselect.task } : null));
  const pendingGrid = useRef(preselect?.grid ? preselect : null);
  const bodyRef = useRef(null);
  const [grids, setGrids] = useState(null);
  const [gridError, setGridError] = useState('');
  const [level, setLevel] = useState('mv');
  const [gridSearch, setGridSearch] = useState('');
  const [grid, setGrid] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    netlabApi.areas()
      .then(d => {
        const list = (d.features || [])
          .map(f => f.properties || {})
          .filter(p => p.has_lv || p.has_mv)
          .sort((a, b) => String(a.name).localeCompare(String(b.name)));
        setAreas(list);
      })
      .catch(e => setAreaError(e.message));
  }, []);

  useEffect(() => {
    if (!area) return;
    let cancelled = false;
    setGrids(null);
    setGrid(null);
    setGridError('');
    netlabApi.taskGrids(area.task_id)
      .then(d => {
        if (cancelled) return;
        setGrids(d.grids);
        const wanted = pendingGrid.current;
        pendingGrid.current = null;
        if (wanted && (d.grids[wanted.level] || []).includes(wanted.grid)) {
          setLevel(wanted.level);
          setGrid(wanted.grid);
          return;
        }
        const first = LEVELS.find(l => (d.grids[l.key] || []).length);
        setLevel(first ? first.key : 'mv');
        const only = first && d.grids[first.key].length === 1 ? d.grids[first.key][0] : null;
        setGrid(only);
      })
      .catch(e => !cancelled && setGridError(e.message));
    return () => { cancelled = true; };
  }, [area]);

  // Bring preselected entries into view once their lists have rendered.
  useEffect(() => {
    if (!preselect) return;
    bodyRef.current?.querySelectorAll('.nl-list-item.active')
      .forEach(el => el.scrollIntoView({ block: 'center' }));
  }, [preselect, areas, grids, level]);

  const filteredAreas = useMemo(() => {
    if (!areas) return [];
    const q = search.trim().toLowerCase();
    return q ? areas.filter(a => String(a.name).toLowerCase().includes(q)) : areas;
  }, [areas, search]);

  const levelGrids = useMemo(() => {
    const list = grids?.[level] || [];
    const q = gridSearch.trim().toLowerCase();
    return q ? list.filter(g => g.toLowerCase().includes(q)) : list;
  }, [grids, level, gridSearch]);

  const open = async () => {
    if (!area || !grid) return;
    setBusy(true);
    setError('');
    try {
      onLoaded(await netlabApi.openNetbank(area.task_id, level, grid));
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <div className="nl-modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="nl-modal nl-netbank" role="dialog" aria-label="Load a grid from NetBank">
        <div className="nl-modal-head">
          <h2>Load from NetBank</h2>
          <button className="nl-icon-btn" onClick={onClose} disabled={busy} aria-label="Close">×</button>
        </div>

        <div className="nl-netbank-cols" ref={bodyRef}>
          <section className="nl-col">
            <div className="nl-col-title">1 · Area</div>
            <input className="nl-search" placeholder="Search areas…" value={search}
              onChange={(e) => setSearch(e.target.value)} autoFocus />
            <div className="nl-list">
              {!areas && !areaError && <div className="nl-list-empty">Loading areas…</div>}
              {areaError && <div className="nl-error">{areaError}</div>}
              {areas && filteredAreas.length === 0 && <div className="nl-list-empty">No areas match.</div>}
              {filteredAreas.map(a => {
                const { kg, gemeinde, bundesland } = nameParts(a.name);
                return (
                  <button key={a.task_id}
                    className={`nl-list-item${area?.task_id === a.task_id ? ' active' : ''}`}
                    onClick={() => setArea(a)}>
                    <span className="nl-li-main">{kg}</span>
                    <span className="nl-li-sub">{[gemeinde, bundesland].filter(Boolean).join(' · ')}</span>
                    <span className="nl-li-tags">
                      {a.has_mv && <span className="nl-tag">MV</span>}
                      {a.has_lv && <span className="nl-tag">LV</span>}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>

          <section className="nl-col">
            <div className="nl-col-title">2 · Grid</div>
            {!area && <div className="nl-list-empty">Pick an area first.</div>}
            {area && gridError && <div className="nl-error">{gridError}</div>}
            {area && !grids && !gridError && <div className="nl-list-empty">Looking for pandapower nets…</div>}
            {grids && (
              <>
                <div className="nl-segmented" role="tablist">
                  {LEVELS.map(l => {
                    const n = (grids[l.key] || []).length;
                    return (
                      <button key={l.key} role="tab" aria-selected={level === l.key}
                        className={level === l.key ? 'active' : ''} disabled={!n}
                        onClick={() => { setLevel(l.key); setGrid(n === 1 ? grids[l.key][0] : null); setGridSearch(''); }}>
                        {l.label} <span className="nl-count">{n}</span>
                      </button>
                    );
                  })}
                </div>
                {(grids[level] || []).length > 8 && (
                  <input className="nl-search" placeholder="Filter transformers…" value={gridSearch}
                    onChange={(e) => setGridSearch(e.target.value)} />
                )}
                <div className="nl-list">
                  {levelGrids.length === 0 && <div className="nl-list-empty">No nets at this level.</div>}
                  {levelGrids.map(g => (
                    <button key={g} className={`nl-list-item compact${grid === g ? ' active' : ''}`}
                      onClick={() => setGrid(g)}>
                      <span className="nl-li-main mono">{g}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </section>
        </div>

        {error && <div className="nl-error">{error}</div>}

        <div className="nl-modal-actions">
          <button className="nl-btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="nl-btn primary" onClick={open} disabled={!grid || busy}>
            {busy ? 'Loading…' : grid ? `Open ${grid}` : 'Open'}
          </button>
        </div>
      </div>
    </div>
  );
}
